import { createSelector } from '@reduxjs/toolkit';
import { filter, get, isEmpty, isNil, uniqueId, toString, values, pick } from 'lodash/fp';
import {
    type Accessible,
    type DeploymentDetails,
    DeploymentOutOfSyncProperty,
    type NotAccessible,
    type SnowflakeTableFqn
} from 'app/lib/types/server';
import type { RootState } from 'app/store/reducers';
import { extractError } from 'app/lib/error';
import { fqnEquals, toStringFqn } from 'app/lib/utils';
import { ErrorLevel, RequestStatus } from 'app/lib/constants';
import {
    selectUpstreamDataSourceFqnsByViewUid,
    selectCurrentProjectId,
    selectSelectedEntityFqn,
    selectAllUpstreamSourceFqns
} from 'app/store/projects/selectors.js';
import {
    createDeepEqualitySelector,
    resolveErrorCategory,
    getTranslatedError
} from 'app/store/utils';
import { selectEntityByPropId } from 'app/store/entities/selectors.js';
import type { ValidationErrorRecord } from 'app/lib/error/types';

const selectEntityIdProp = (_: RootState, props: { entityId: string | number }) => props.entityId;
const selectEntityFqnProp = (_: RootState, props: { fqn: string }) => props.fqn;

const selectEntityFqnString = createSelector(selectEntityFqnProp, toStringFqn);

const selectSlice = (state: RootState) => state.access;
const selectValidationsBySourceFqn = createSelector(
    selectSlice,
    ({ validationsBySourceFqn }) => validationsBySourceFqn
);

export const selectValidationsValuesBySourceFqn = createSelector(
    selectValidationsBySourceFqn,
    ({ values }) => values
);

export const selectValidationStatusesBySourceFqn = createSelector(
    selectValidationsBySourceFqn,
    ({ statuses }) => statuses
);

export const selectValidationValueBySourceFqn = createSelector(
    selectEntityFqnString,
    selectValidationsValuesBySourceFqn,
    (fqn, validationsBySourceFqn) => fqn && validationsBySourceFqn?.[fqn]
);

export const selectExecutionTriggerForPropFqn = createSelector(
    selectValidationValueBySourceFqn,
    (validation) => {
        if (!validation) {
            return null;
        }

        if ('trigger' in validation) {
            return validation.trigger;
        }
    }
);

const selectSelectedEntityValidation = createSelector(
    selectValidationsValuesBySourceFqn,
    selectSelectedEntityFqn,
    (validationsBySourceFqn, fqn) => {
        const stringFqn = fqn && toStringFqn(fqn);
        if (isNil(stringFqn)) {
            return null;
        }
        return fqn && validationsBySourceFqn?.[stringFqn];
    }
);

export const selectDeploymentRoleAndWarehouseForPropFqn = createSelector(
    selectValidationsValuesBySourceFqn,
    selectEntityFqnString,
    (validationsBySourceFqn, fqn) => {
        if (isNil(fqn) || isEmpty(validationsBySourceFqn?.[fqn])) {
            return {};
        }
        const source = validationsBySourceFqn?.[fqn];

        if ('role' in source && 'warehouse' in source) {
            return { role: source.role, warehouse: source.warehouse };
        }
        return {};
    }
);

export const selectSelectedEntityValidationValueRole = createSelector(
    selectSelectedEntityValidation,
    get('role')
);

export const selectSelectedEntityValidationValueWarehouse = createSelector(
    selectSelectedEntityValidation,
    get('warehouse')
);

export const selectSelectedEntityValidationValueAccessible = createSelector(
    selectSelectedEntityValidation,
    get('accessible')
);

const selectHasNoUpstreamSources = createSelector(
    selectEntityIdProp,
    selectUpstreamDataSourceFqnsByViewUid,
    (entityId, upstreamSourcesFqnsByViewId) => {
        const [, viewId] = toString(entityId).split('.');
        const sources = upstreamSourcesFqnsByViewId?.[viewId!];

        return isEmpty(sources);
    }
);

function getLevelForIssueType(type: DeploymentOutOfSyncProperty) {
    switch (type) {
        case DeploymentOutOfSyncProperty.COLUMNS_ADDED_UPSTREAM:
        case DeploymentOutOfSyncProperty.TRANSFORMATION_SQL:
            return ErrorLevel.WARNING;
        case DeploymentOutOfSyncProperty.COLUMNS_REMOVED_UPSTREAM:
        case DeploymentOutOfSyncProperty.COLUMNS_TYPE_CHANGED_UPSTREAM:
            return ErrorLevel.ERROR;
        default:
            return ErrorLevel.OK;
    }
}
export const selectAllFailedDataSourcesFqns = createDeepEqualitySelector(
    selectValidationsValuesBySourceFqn,
    (validationsBySourceFqn) => {
        const fqnObjects = [];
        for (const [, validation] of Object.entries(validationsBySourceFqn)) {
            const fqnObject = pick(['database', 'schema', 'table'], validation);
            if (
                ('deploymentOutOfSync' in validation && !isEmpty(validation.deploymentOutOfSync)) ||
                ('failures' in validation && !isEmpty(validation.failures))
            ) {
                fqnObjects.push(fqnObject);
            }
        }
        return fqnObjects;
    }
);

function createSyncStatusDescriptor(type: DeploymentOutOfSyncProperty, values = {}) {
    return {
        type,
        id: uniqueId('s'),
        level: getLevelForIssueType(type),
        values,
        category: resolveErrorCategory(type)
    };
}

export const selectSourceValidationErrors = createSelector(
    selectValidationsValuesBySourceFqn,
    (validations) => {
        if (isEmpty(validations)) {
            return {};
        }
        const resultObj: ValidationErrorRecord = {};
        const fqns = Object.keys(validations);
        fqns.forEach((fqn) => {
            const validation = validations?.[fqn];
            resultObj[fqn] = [];
            if (isNil(validation)) {
                return resultObj[fqn];
            }
            const result = [];
            if ('failures' in validation && !isEmpty(validation.failures)) {
                const extractedError = extractError(validation, ErrorLevel.WARNING);
                result.push({
                    ...extractedError,
                    values: {
                        message:
                            getTranslatedError(extractedError) ?? extractedError.originalMessage
                    },
                    type: 'GENERIC_WARNING'
                });
            }

            if (!('deploymentOutOfSync' in validation)) {
                resultObj[fqn] = result;
                return resultObj[fqn];
            }
            const { columnsDiff, deploymentOutOfSync } = validation;

            for (const issueType of deploymentOutOfSync) {
                switch (issueType) {
                    case DeploymentOutOfSyncProperty.COLUMNS_ADDED_UPSTREAM:
                        if (columnsDiff) {
                            for (const columnName of columnsDiff.added) {
                                result.push(createSyncStatusDescriptor(issueType, { columnName }));
                            }
                        }
                        break;
                    case DeploymentOutOfSyncProperty.COLUMNS_REMOVED_UPSTREAM:
                        if (columnsDiff) {
                            for (const columnName of columnsDiff.removed) {
                                result.push(createSyncStatusDescriptor(issueType, { columnName }));
                            }
                        }
                        break;
                    case DeploymentOutOfSyncProperty.COLUMNS_TYPE_CHANGED_UPSTREAM:
                        if (columnsDiff) {
                            for (const [columnName, typeChange] of Object.entries(
                                columnsDiff.typeChanged
                            )) {
                                const { typeBefore, typeAfter } = typeChange;
                                result.push(
                                    createSyncStatusDescriptor(issueType, {
                                        columnName,
                                        typeBefore,
                                        typeAfter
                                    })
                                );
                            }
                        }
                        break;
                    case DeploymentOutOfSyncProperty.TRANSFORMATION_SQL:
                        result.push(createSyncStatusDescriptor(issueType));
                        break;
                    default:
                        break;
                }
            }

            resultObj[fqn] = result;
        });

        return resultObj;
    }
);

const selectAllValidationsForPropEntityId = createSelector(
    selectValidationsValuesBySourceFqn,
    selectUpstreamDataSourceFqnsByViewUid,
    selectEntityIdProp,
    (validationsBySourceId, upstreamSourcesFqnsByViewId, entityId) => {
        // If the entityId is a datasource, we can try to pull it directly from
        // the access validations list
        const datasourceValidation = validationsBySourceId?.[entityId];
        if (datasourceValidation) {
            return [datasourceValidation];
        }
        const [, viewId] = toString(entityId).split('.');

        if (upstreamSourcesFqnsByViewId?.hasOwnProperty(viewId)) {
            const sourceFqns = upstreamSourcesFqnsByViewId[viewId!];

            const validations = [];
            const validationsValues = Object.values(validationsBySourceId ?? {});

            for (const sourceFqn of sourceFqns) {
                const validationForSource = validationsValues.find((value) =>
                    fqnEquals(value, sourceFqn)
                );

                if (validationForSource) {
                    validations.push(validationForSource);
                } else {
                    return null;
                }
            }

            return validations;
        }

        return [];
    }
);

export const selectResolvedValidationsForSourceId = createSelector(
    selectValidationsValuesBySourceFqn,
    selectValidationStatusesBySourceFqn,
    selectUpstreamDataSourceFqnsByViewUid,
    selectCurrentProjectId,
    (
        validationsValuesBySourceId,
        validationStatusesBySourceFqn,
        upstreamSourcesFqnsByViewId,
        projectId
    ) => {
        const result: { [index: string]: Array<Accessible | NotAccessible | DeploymentDetails> } =
            {};
        const validationsValues = values(validationsValuesBySourceId);

        for (const viewId in upstreamSourcesFqnsByViewId) {
            const entityId = `${projectId}.${viewId}`;

            if (!result[entityId]) {
                result[entityId] = [];
            }

            const sourceFqns = upstreamSourcesFqnsByViewId?.[viewId];

            if (!sourceFqns) {
                continue;
            }

            sourceFqns.forEach((sourceFqn: SnowflakeTableFqn) => {
                if (
                    validationStatusesBySourceFqn?.[toStringFqn(sourceFqn)] ===
                    RequestStatus.RESOLVED
                ) {
                    const validationValue = validationsValues.find((value) =>
                        fqnEquals(value, sourceFqn)
                    );

                    if (validationValue) {
                        result[entityId]!.push(validationValue);
                    }
                }
            });
        }

        return result;
    }
);

export const selectValidationsOfCurrentProject = createSelector(
    selectValidationsValuesBySourceFqn,
    selectAllUpstreamSourceFqns,
    (validationsValuesBySourceId, projectSources) => {
        const result = [];

        for (const fqn of projectSources) {
            const fqnString = toStringFqn(fqn);
            const validation = validationsValuesBySourceId?.[fqnString];
            if (validation) {
                result.push(validation);
            }
        }

        return result;
    }
);

export const selectHasAccessibleDataSourcesForPropEntityId = createSelector(
    selectAllValidationsForPropEntityId,
    selectHasNoUpstreamSources,
    (validations, hasNoUpstreamSources) => {
        if (hasNoUpstreamSources) {
            return true;
        }

        if (isNil(validations) || isEmpty(validations)) {
            return null;
        }

        return validations.every((v) => 'accessible' in v && v.accessible);
    }
);

export const selectInaccessibleDataSourcesForPropEntityId = createSelector(
    selectAllValidationsForPropEntityId,
    selectHasNoUpstreamSources,
    (validations, hasNoUpstreamSources) => {
        if (hasNoUpstreamSources || isEmpty(validations)) {
            return null;
        }

        return filter({ accessible: false }, validations);
    }
);

export const selectIsValidationPendingBySourceFqn = createSelector(
    selectEntityFqnString,
    selectValidationStatusesBySourceFqn,
    (fqn, statusValidationsBySourceFqn) =>
        fqn && statusValidationsBySourceFqn?.[fqn] === RequestStatus.PENDING
);

function getDataSizeInfo(
    validationsForSourceId: {
        [index: string]: Array<Accessible | NotAccessible | DeploymentDetails>;
    },
    entity?: {
        entityId?: string;
        tableFqn?: SnowflakeTableFqn;
    }
) {
    if (entity?.entityId && entity.entityId in validationsForSourceId) {
        if (entity.tableFqn !== undefined) {
            const validation = validationsForSourceId[entity.entityId]?.find((validation) =>
                fqnEquals(validation, entity.tableFqn)
            );

            if (
                validation &&
                'sampleSize' in validation &&
                'rowCount' in validation &&
                'tableType' in validation
            ) {
                const { rowCount: recordCount, sampleSize, tableType: type } = validation;

                return { recordCount, sampleSize, type };
            }

            return null;
        }

        const sampledInputTables = [];
        const inputValidations = validationsForSourceId[entity.entityId]!;

        for (const inputValidation of inputValidations) {
            if ('sampleSize' in inputValidation && !isNil(inputValidation.sampleSize)) {
                sampledInputTables.push(inputValidation.table);
            }
        }

        return { sampledInputTables };
    }

    return null;
}

export const selectDatasizeInfoByPropId = createSelector(
    selectEntityByPropId,
    selectResolvedValidationsForSourceId,
    (entity, validationsForSourceId) => getDataSizeInfo(validationsForSourceId, entity)
);
